import React, { useMemo } from 'react';

import BaseSettingCard from '@dashboard/components/platform-settings/BaseSettingCard';
import {
  SesEventName,
  type UpdatePlatformSesEventTrackingMutationVariables,
  usePlatformSesConfigurationQuery,
  useUpdatePlatformSesEventTrackingMutation,
} from '@dashboard/generated/wp/platform';
import { FormControl, FormControlLabel, FormGroup, Stack, Switch, Typography } from '@mui/material';

import { alertError, alertSuccess } from '@waypoint/react/notifications';

type TPayload = {
  click: boolean;
  open: boolean;
};

type TEvents = UpdatePlatformSesEventTrackingMutationVariables['events'];

export default function EventsTrackingCard() {
  const [executeMutation, { loading }] = useUpdatePlatformSesEventTrackingMutation();
  const { data } = usePlatformSesConfigurationQuery();
  const events = data?.PlatformSesConfiguration.events;
  const open = useMemo(() => events?.includes(SesEventName.Open) ?? false, [events]);
  const click = useMemo(() => events?.includes(SesEventName.Click) ?? false, [events]);

  const onSubmit = async (payload: TPayload) => {
    const events: TEvents = [];
    if (payload.click) {
      events.push(SesEventName.Click);
    }
    if (payload.open) {
      events.push(SesEventName.Open);
    }
    try {
      await executeMutation({ variables: { events } });
      alertSuccess('Event tracking updated');
    } catch {
      alertError('Failed to update event tracking');
    }
  };

  const controlLabelSx = { gap: 1, alignItems: 'flex-start' };

  return (
    <BaseSettingCard title="Tracking" helpUrl="https://www.usewaypoint.com/docs/event-tracking">
      <FormControl component="fieldset" variant="standard">
        <FormGroup sx={{ gap: 2.5 }}>
          <FormControlLabel
            label={
              <Stack spacing={0.5}>
                <Typography>Open events</Typography>
                <Typography variant="body2" color="text.secondary">
                  Adds a 1x1 pixel transparent image to your message to track every time someone opens an email.
                </Typography>
              </Stack>
            }
            labelPlacement="end"
            sx={controlLabelSx}
            control={
              <Switch
                disabled={events === undefined || loading}
                checked={open ?? false}
                size="small"
                onChange={(ev, checked: boolean) => {
                  if (!events) return;
                  onSubmit({ click, open: checked });
                }}
              />
            }
          />
          <FormControlLabel
            label={
              <Stack spacing={0.5}>
                <Typography>Click events</Typography>
                <Typography variant="body2" color="text.secondary">
                  Modifies all links in your message to use a proxy URL to record the click before redirecting to the
                  destination.
                </Typography>
              </Stack>
            }
            labelPlacement="end"
            sx={controlLabelSx}
            control={
              <Switch
                disabled={events === undefined || loading}
                checked={click ?? false}
                size="small"
                onChange={(ev, checked: boolean) => {
                  if (!events) return;
                  onSubmit({ open, click: checked });
                }}
              />
            }
          />
        </FormGroup>
      </FormControl>
    </BaseSettingCard>
  );
}
